import { MutationCache, QueryClient } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import { makeTrpcClient, trpc } from '@tg/app-core';
import superjson from 'superjson';
import { useState, type ReactNode } from 'react';

/* Данные — только через API. Оффлайн (CLI-3) пока минимальный: кэш React Query
   сохраняется в localStorage, и открытые ранее дни, неделя, профиль и база
   упражнений показываются без сети. Полноценный синк — позже через PowerSync,
   своего синка здесь нет: мутации без сети ждут сеть (networkMode 'online'). */

const WEEK = 7 * 864e5;
const CACHE_KEY = 'tg.client.cache';

/** Что кладём на устройство: тренировки, календарь, профиль, лента, база упражнений */
const PERSIST = new Set([
  'clientApp.me',
  'clientApp.day',
  'clientApp.days',
  'clientApp.calendar',
  'clientApp.maxes',
  'clientApp.measurements',
  'clientApp.feed',
  'catalog.exercises',
  'catalog.exerciseDetail',
]);

const safeStorage = {
  getItem: (k: string) => {
    try {
      return localStorage.getItem(k);
    } catch {
      return null;
    }
  },
  setItem: (k: string, v: string) => {
    try {
      localStorage.setItem(k, v);
    } catch {
      /* переполнено или недоступно — живём без оффлайн-кэша */
    }
  },
  removeItem: (k: string) => {
    try {
      localStorage.removeItem(k);
    } catch {
      /* недоступно */
    }
  },
};

/** Действия, которые закрывают «Первые шаги» (H0): после них профиль (clientApp.me, там шаги) перечитывается сразу —
    шаг отмечается без перезагрузки, где бы клиент его ни сделал (профиль, тренировка) */
const STEP_MUTATIONS = new Set(['addMax', 'addMeasurement', 'updateProfile', 'finishDay', 'markPwa']);

export const queryClient: QueryClient = new QueryClient({
  mutationCache: new MutationCache({
    onSuccess: (_d, _v, _c, m) => {
      const k = (m.options.mutationKey as string[][] | undefined)?.[0];
      if (k?.[0] === 'clientApp' && STEP_MUTATIONS.has(k[1])) queryClient.invalidateQueries({ queryKey: [['clientApp', 'me']] });
    },
  }),
  defaultOptions: {
    queries: { staleTime: 30_000, gcTime: WEEK, retry: 1, refetchOnWindowFocus: true, networkMode: 'offlineFirst' },
  },
});

const persister = createSyncStoragePersister({
  storage: safeStorage,
  key: CACHE_KEY,
  throttleTime: 2000,
  serialize: (x) => superjson.stringify(x),
  deserialize: (s) => superjson.parse(s),
});

/** Выход и удаление аккаунта: чужие тренировки на устройстве не оставляем */
export function clearCache() {
  queryClient.clear();
  safeStorage.removeItem(CACHE_KEY);
}

const pathOf = (key: readonly unknown[]) => (Array.isArray(key[0]) ? (key[0] as string[]).join('.') : '');

export function DataProvider({ children }: { children: ReactNode }) {
  const [client] = useState(makeTrpcClient);
  return (
    <trpc.Provider client={client} queryClient={queryClient}>
      <PersistQueryClientProvider
        client={queryClient}
        persistOptions={{
          persister,
          maxAge: WEEK,
          dehydrateOptions: { shouldDehydrateQuery: (q) => q.state.status === 'success' && PERSIST.has(pathOf(q.queryKey)) },
        }}
      >
        {children}
      </PersistQueryClientProvider>
    </trpc.Provider>
  );
}
